import { useMemo } from "react"
import { Area, AreaChart, ResponsiveContainer, Bar, BarChart } from "recharts"
import cn from "@/v2-ui/utils/utils.cn"

// Faint background sparklines that sit behind each KPI card's value.
// Pure decoration -- the value text owns the actual reading; the
// sparkline just adds a sense of "what does this look like over time"
// without making the operator open a dedicated chart.
//
// We use ResponsiveContainer + an absolutely-positioned wrapper so
// the chart fills the card edge-to-edge regardless of card padding,
// and so it never affects the natural flow of the value/sub text.
//
// Contrast is tuned so the chart reads on both the dark cards
// (which win on most of the dashboard) and the lighter "OK" status
// card. Bars use a flat fill with rounded tops so even single-day
// spikes are obvious; areas use a stroke + gradient fill so a
// monotonic curve has a visible "ridge".

export type UptimeOverviewSparklineProps = {
  // Numeric series, one number per bucket. Nulls render as gaps.
  series: Array<number | null>
  variant: "primary" | "destructive" | "warning" | "violet" | "success"
  // Visual style. "area" suits monotonic curves (uptime, latency);
  // "bar" suits discrete counts (incidents per day).
  shape?: "area" | "bar"
  className?: string
}

// Per-variant colour pair: a strong "main" colour for strokes / bar
// fills, and a brighter "tint" used at the top of the area gradient.
// Both are explicit hex/rgb values rather than CSS vars so the
// Recharts SVG can resolve them outside of the React tree.
//
// `primary` (used for the latency card) leans on a high-luminance
// indigo so the curve sits clearly above the dark card background --
// the previous tint was too close to the card colour and read as a
// flat smear.
const COLOR_BY_VARIANT: Record<
  UptimeOverviewSparklineProps["variant"],
  { main: string; tint: string }
> = {
  primary: { main: "rgb(129, 140, 248)", tint: "rgb(165, 180, 252)" },
  destructive: { main: "rgb(248, 113, 113)", tint: "rgb(252, 165, 165)" },
  warning: { main: "rgb(251, 191, 36)", tint: "rgb(253, 224, 71)" },
  violet: { main: "rgb(167, 139, 250)", tint: "rgb(196, 181, 253)" },
  success: { main: "rgb(74, 222, 128)", tint: "rgb(134, 239, 172)" },
}

function UptimeOverviewSparkline(
  props: Readonly<UptimeOverviewSparklineProps>,
) {
  const { series, variant, shape = "area", className } = props

  // Recharts wants object data even for single-series sparklines.
  // Stringifying the index also gives us a stable xAxis key.
  const data = useMemo(() => series.map((v, i) => ({ i, v })), [series])

  // Empty-state: nothing to draw, so render nothing rather than a
  // dead flat line that'd just add visual noise to the card.
  const hasAny = series.some((v) => v != null)
  if (!hasAny) return null

  const { main, tint } = COLOR_BY_VARIANT[variant]
  const gradId = `uos-${variant}-${shape}`

  return (
    <div
      aria-hidden
      className={cn(
        "uptime-overview-card-sparkline",
        "uptime-overview-card-sparkline-area",
        className,
      )}
    >
      <ResponsiveContainer width="100%" height="100%">
        {shape === "bar" ? (
          // Bar charts spend most of their height at zero on quiet
          // days, so we lean on a high-opacity fill (0.7) and a
          // small radius rather than a stroke to make individual
          // bars read against the dark card.
          <BarChart
            data={data}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <Bar
              dataKey="v"
              fill={main}
              fillOpacity={0.7}
              isAnimationActive={false}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        ) : (
          // Area charts always have something to render (we connect
          // nulls), so we can afford a softer fill but keep a strong
          // stroke for the "ridge line" that gives the curve shape.
          <AreaChart
            data={data}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={tint} stopOpacity={0.85} />
                <stop offset="100%" stopColor={main} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="v"
              stroke={main}
              strokeWidth={2.25}
              strokeOpacity={1}
              fill={`url(#${gradId})`}
              isAnimationActive={false}
              connectNulls
              dot={false}
              activeDot={false}
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  )
}

export default UptimeOverviewSparkline
